<template>
  <div>
    <div
      class="video-container"
      ref="app"
      @mousedown="startDrawingBox"
      @mousemove="changeBox"
      @mouseup="stopDrawingBox"
    >
      <Box
        v-show="showBoxes && !disabled"
        v-if="drawingBox.active"
        :b-width="drawingBox.width"
        :b-height="drawingBox.height"
        :b-top="drawingBox.top"
        :b-left="drawingBox.left"
      />

      <Box
        v-show="showBoxes"
        v-for="(box, i) in scaledBoxes"
        :key="i"
        :b-top="box.top"
        :b-left="box.left"
        v-bind:b-label="box.name"
        :b-width="box.width"
        :b-height="box.height"
        :b-active="i === activeBoxIndex"
        :on-select="makeBoxActive"
        :b-index="i"
        :on-delete="removeBox"
        @updated="updateLabel"
        :disabled="disabled"
      />

      <img
        id="image"
        :src="variant === 'Heatmap' ? heatmap : peekthrough"
        alt=""
        v-bind:style="{
          backgroundColor: 'rgba(9,49,240, ' + opacity / 100 + ')',
        }"
      />
      <img
        v-if="this.variant === 'Heatmap'"
        style="position: absolute; top: 0; left: 0; z-index: -1;"
        id="image"
        :src="image"
        alt=""
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.video-container {
  position: relative;
  max-width: 1200px;
  max-height: 100%;
}

.heatmapjs-container {
  position: absolute;
  height: 100%;
  z-index: 6;
  pointer-events: none;
}

.imagewrapper {
  width: 100%;
}

img {
  user-drag: none;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}
</style>

<script>
import Box from "./box";
import { pick } from "lodash";

const getCoursorLeft = (e) => {
  return e.pageX - 540;
};

const getCoursorTop = (e) => {
  return e.pageY - 121;
};

export default {
  props: [
    "image",
    "data",
    "imgwidth",
    "imgheight",
    "originalWidth",
    "showBoxes",
    "disabled",
    "variant",
    "loadBoxes",
    "image_result",
    "heatmap",
    "opacity",
    "peekthrough",
  ],
  components: {
    Box,
  },
  data() {
    return {
      activeBoxIndex: null,
      show: true,
      width: 0,
      height: 0,
      max: 255,
      min: 0,
      moveDrawable: false,
      clickDrawable: false,
      drawValue: 5,

      drawingBox: {
        active: false,
        top: 0,
        left: 0,
        height: 0,
        width: 0,
      },
      boxes: [],
      refWidth: 0,
      mice: [],
      screenshot: null,
      heatmaps: [],
      total: 0,
    };
  },
  watch: {
    activeBoxIndex: function() {
      if (this.activeBoxIndex != null) {
        this.$emit("onSelectBox", this.activeBoxIndex);
      } else if (this.activeBoxIndex == null) {
        this.$emit("onSelectBox", "-", "-", "-", "-");
      }
    },
    boxes: {
      deep: true,
      handler() {
        this.$emit("boxUpdate", this.boxes);
      },
    },
  },
  mounted() {
    var ro = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const cr = entry.contentRect;
        this.width = cr.width;
        this.refWidth = cr.width;
        this.height = cr.height;
      }
    });

    // Observe one or multiple elements
    ro.observe(document.querySelector("#image"));
    if (this.loadBoxes) {
      this.loadBoxes.forEach(async (el) => {
        this.boxes.push({
          width: el.width,
          height: el.height,
          top: el.top,
          left: el.left,
          value: await this.getBoxValue(el),
          name: el.name,
          id: el.id,
        });
      });
    }
  },
  computed: {
    scale: function() {
      return this.width / this.originalWidth;
    },
    radius: function() {
      return 61 * this.scale;
    },
    blur: function() {
      return 0.6;
    },
    options: function() {
      const options = {
        radius: this.radius,
        blur: 1,
        gradient: {
          // enter n keys between 0 and 1 here
          // for gradient color customization
          ".3": "rgba(9, 49, 240, 1)",
          "0.50": "#00DEAB",
          ".8": "#F1C40F",
          ".9": "#FF14A9",
        },
      };

      const optionsPeekthrough = {
        radius: this.radius,
        blur: this.blur,
        minOpacity: 0,
        maxOpacity: 1,
        gradient: {
          // enter n keys between 0 and 1 here
          // for gradient color customization
          ".5": "transparent",
          ".51": "#141A77",
        },
      };
      if (this.variant === "Heatmap") {
        return options;
      } else {
        return optionsPeekthrough;
      }
    },
    eyeData: function() {
      let scale = this.width / this.originalWidth;
      let newEyes = [];

      this.data.forEach((element) => {
        newEyes.push({
          x: Math.floor(element.x * scale),
          y: Math.floor(element.y * scale),
          value: Math.floor(element.value * scale),
        });
      });

      let data = [];
      newEyes.forEach((el) => {
        let value = null;
        if (el.value / scale < 40) {
          value = 255;
        }
        data.push({
          x: el.x,
          y: el.y,
          value: value * scale,
        });
      });

      if (this.variant === "Heatmap") {
        return newEyes;
      } else {
        return data;
      }
    },
    totalValue: function() {
      let total = 0;
      this.data.forEach((el) => {
        total = total + el.value;
      });
      return total;
    },
    boxData: function() {
      const box = this.scaledBoxes[this.activeBoxIndex];
      let filtered;
      if (this.activeBoxIndex != null) {
        filtered = this.eyeData.filter(xy);
      }

      function xy(obj) {
        if (obj.y < box.top || obj.y > box.top + box.height) {
          return false;
        } else if (obj.x < box.left || obj.x > box.left + box.width) {
          return false;
        } else {
          return true;
        }
      }
      return filtered;
    },
    scaledBoxes: function() {
      let boxes = this.boxes;

      let converted = [];
      if (boxes.length > 0) {
        boxes.forEach((el) => {
          converted.push({
            height: Math.floor(el.height * this.scale),
            width: Math.floor(el.width * this.scale),
            left: Math.floor(el.left * this.scale),
            top: Math.floor(el.top * this.scale),
            name: el.name,
          });
        });
      }

      return converted;
    },
  },
  methods: {
    getBoxValue(box) {
      let score = 0;
      function xy(obj) {
        if (obj.y < box.top || obj.y > box.top + box.height) {
          return false;
        } else if (obj.x < box.left || obj.x > box.left + box.width) {
          return false;
        } else {
          return true;
        }
      }
      let value = 0;

      let filtered = this.data.filter(xy);
      filtered.forEach((el) => {
        value = value + el.value;
      });

      score = value / this.totalValue;

      return score;
    },
    getActiveBoxValue() {
      let score = 0;
      const box = {
        height: this.drawingBox.height / this.scale,
        left: this.drawingBox.left / this.scale,
        top: this.drawingBox.top / this.scale,
        width: this.drawingBox.width / this.scale,
      };

      function xy(obj) {
        if (obj.y < box.top || obj.y > box.top + box.height) {
          return false;
        } else if (obj.x < box.left || obj.x > box.left + box.width) {
          return false;
        } else {
          return true;
        }
      }

      if (this.drawingBox.active) {
        let value = 0;

        let filtered = this.data.filter(xy);

        filtered.forEach((el) => {
          value = value + el.value;
        });

        score = value / this.totalValue;
      }
      return score;
    },
    startDrawingBox(e) {
      if (this.showBoxes && !this.disabled) {
        this.drawingBox = {
          width: 0,
          height: 0,
          top: getCoursorTop(e),
          left: getCoursorLeft(e),
          active: true,
        };
      }
    },
    changeBox(e) {
      if (this.showBoxes && !this.disabled) {
        if (this.drawingBox.active) {
          this.drawingBox = {
            ...this.drawingBox,
            width: getCoursorLeft(e) - this.drawingBox.left,
            height: getCoursorTop(e) - this.drawingBox.top,
          };
        }
      }
    },
    makeBoxActive(i) {
      this.activeBoxIndex = i;
    },
    removeBox(i) {
      this.$emit("delete", this.boxes[i]);
      this.boxes = this.boxes.filter((elem, index) => {
        return index !== i;
      });

      this.activeBoxIndex = null;
    },
    async stopDrawingBox() {
      if (this.drawingBox.active && this.showBoxes && !this.disabled) {
        if (this.drawingBox.width > 5) {
          const newBox = {
            width: this.drawingBox.width / this.scale,
            height: this.drawingBox.height / this.scale,
            top: this.drawingBox.top / this.scale,
            left: this.drawingBox.left / this.scale,
            value: await this.getActiveBoxValue(),
            name: `Box ${this.boxes.length + 1}`,
          };
          this.boxes.push(newBox);
          this.$emit("newBox", newBox);
        }
        this.drawingBox = {
          active: false,
          top: 0,
          left: 0,
          height: 0,
          width: 0,
        };
      }
    },
    updateLabel(val, index) {
      if (val) {
        this.boxes[index].name = val;
      } else {
        this.boxes[index].name = `Box ${index}`;
      }
      this.$emit("updateName", this.boxes[index]);
    },
  },
};
</script>

<style lang="scss" scoped>
#label-bar {
  float: right;
  margin-right: 50px;
  width: 220px;

  ul {
    padding: 0;

    li {
      list-style-type: none;
      padding: 8px 16px;

      &.active {
        background-color: lightblue;
      }

      a {
        cursor: pointer;
        display: inline-block;
        margin-left: 4px;
        font-weight: bold;
        color: red;
      }
    }
  }
}
</style>
